
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Loader2, Mail, CheckCircle, ArrowLeft } from 'lucide-react';
import { useAuthStore } from '@/lib/stores/auth';
import { Link } from 'react-router-dom';

const magicLinkSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type MagicLinkFormValues = z.infer<typeof magicLinkSchema>;

export function MagicLinkForm() {
  const [isSuccess, setIsSuccess] = useState(false);
  const [sentEmail, setSentEmail] = useState('');
  const { sendMagicLink, error, clearError, isLoading } = useAuthStore();

  const form = useForm<MagicLinkFormValues>({
    resolver: zodResolver(magicLinkSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (values: MagicLinkFormValues) => {
    try {
      clearError();
      await sendMagicLink(values.email);
      setSentEmail(values.email);
      setIsSuccess(true);
    } catch {
      // Error is already set in the store
    }
  };

  if (isSuccess) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center space-y-4 py-6 text-center">
            <CheckCircle className="h-12 w-12 text-success" />
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">Check your email</h3>
              <p className="text-sm text-muted-foreground">
                We&apos;ve sent a sign-in link to <strong>{sentEmail}</strong>.
                Click the link in your email to sign in.
              </p>
              <p className="text-xs text-muted-foreground mt-4">
                Didn&apos;t receive it? Check your spam folder or{' '}
                <button 
                  onClick={() => setIsSuccess(false)}
                  className="underline hover:text-primary"
                >
                  try again
                </button>
              </p>
            </div>
            <Link to="/signin">
              <Button variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to sign in
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="mx-auto grid w-full gap-6">
      <Card className="mx-auto w-full">
        <CardHeader className="space-y-1">
          <CardTitle className="text-xl text-center font-semibold">Sign in with email</CardTitle>
          <CardDescription className="text-center">
            Enter your email and we&apos;ll send you a magic link to sign in
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          placeholder="you@example.com"
                          type="email"
                          className="pl-9"
                          disabled={isLoading}
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full justify-center" disabled={isLoading}>
                {isLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4" />
                )}
                Send magic link
              </Button>
            </form>
          </Form>

          <div className="text-center text-sm text-muted-foreground">
            Prefer a password?{' '}
            <Link
              to="/signin"
              className="underline underline-offset-4 hover:text-primary"
            >
              Sign in with password
            </Link>
          </div>
        </CardContent>
      </Card>

      <div className="text-center text-sm text-muted-foreground">
        <Link to="/" className="hover:text-primary">
          ← Back to home
        </Link>
      </div>
    </div>
  );
}
